/**
 * Markdown simples e seguro (sem HTML cru) para respostas do Assistente: títulos, listas, blocos de código,
 * **negrito**, *itálico*, `código` e citações. Não interpreta links nem imagens (evita conteúdo externo).
 */
import type { ReactNode } from "react";

function inline(text: string, key: string): ReactNode[] {
  const out: ReactNode[] = [];
  const re = /(\*\*[^*]+\*\*|`[^`]+`|\*[^*\s][^*]*\*)/g;
  let last = 0; let m: RegExpExecArray | null; let i = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const t = m[0];
    if (t.startsWith("**")) out.push(<strong key={`${key}-${i++}`}>{t.slice(2, -2)}</strong>);
    else if (t.startsWith("`")) out.push(<code key={`${key}-${i++}`}>{t.slice(1, -1)}</code>);
    else out.push(<em key={`${key}-${i++}`}>{t.slice(1, -1)}</em>);
    last = m.index + t.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export function Markdown({ text }: { text: string }): JSX.Element {
  const lines = text.replace(/\r/g, "").split("\n");
  const blocks: ReactNode[] = [];
  let i = 0; let k = 0;
  while (i < lines.length) {
    const l = lines[i]!;
    if (l.trim().startsWith("```")) {
      const buf: string[] = []; i++;
      while (i < lines.length && !lines[i]!.trim().startsWith("```")) buf.push(lines[i++]!);
      i++;
      blocks.push(<pre key={k++} className="md-pre">{buf.join("\n")}</pre>);
      continue;
    }
    const h = /^(#{1,4})\s+(.*)$/.exec(l);
    if (h) { blocks.push(<div key={k++} className={`md-h md-h${h[1]!.length}`}>{inline(h[2]!, `h${k}`)}</div>); i++; continue; }
    if (/^\s*([-*•]|\d+[.)])\s+/.test(l)) {
      const ordered = /^\s*\d+[.)]/.test(l);
      const items: ReactNode[] = [];
      while (i < lines.length && /^\s*([-*•]|\d+[.)])\s+/.test(lines[i]!)) {
        items.push(<li key={items.length}>{inline(lines[i]!.replace(/^\s*([-*•]|\d+[.)])\s+/, ""), `l${k}-${items.length}`)}</li>);
        i++;
      }
      blocks.push(ordered ? <ol key={k++}>{items}</ol> : <ul key={k++}>{items}</ul>);
      continue;
    }
    if (/^\s*>/.test(l)) {
      const buf: string[] = [];
      while (i < lines.length && /^\s*>/.test(lines[i]!)) buf.push(lines[i++]!.replace(/^\s*>\s?/, ""));
      blocks.push(<blockquote key={k++}>{inline(buf.join(" "), `q${k}`)}</blockquote>);
      continue;
    }
    if (!l.trim()) { i++; continue; }
    const buf: string[] = [];
    while (i < lines.length && lines[i]!.trim() && !/^(#{1,4}\s|\s*([-*•]|\d+[.)])\s|\s*>|\s*```)/.test(lines[i]!)) buf.push(lines[i++]!);
    blocks.push(<p key={k++}>{buf.flatMap((b, j) => (j ? [<br key={`br${j}`} />, ...inline(b, `p${k}-${j}`)] : inline(b, `p${k}-${j}`)))}</p>);
  }
  return <div className="md">{blocks}</div>;
}
